// common/hooks/useProvinces.ts
//
// React Query hook for a country's provinces/states (GET /countries/:id/provinces).
// Geo data never changes, so it's cached with a long staleTime and shared across
// mobile + web edit-player and roster-player flows (keyed by countryId). The
// return shape is kept backward-compatible with the previous useState/useEffect
// implementation so existing consumers don't need changes.

import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '../config/queryKeys'
import { listProvinces, type Province } from '../services/geo/listProvinces'

export type { Province }

// 1 hour — provinces are static reference data; no need to refetch within a session.
const STALE_TIME = 60 * 60 * 1000

// Stable empty-array reference for the no-data case. Returning a fresh `[]` each
// render would change `provinces` identity every render, and consumers key
// effects on `[provinces]` (ProvinceSelectInput feeds it back into parent state)
// — an unstable reference there causes an infinite re-render loop. One shared
// frozen constant keeps the empty result referentially stable.
const EMPTY_PROVINCES: readonly Province[] = Object.freeze([])

export const useProvinces = (countryId?: string | number) => {
  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: queryKeys.provinces.byCountry(countryId),
    queryFn: () => listProvinces(countryId as string | number),
    enabled: !!countryId,
    staleTime: STALE_TIME,
  })

  return {
    provinces: data ?? (EMPTY_PROVINCES as Province[]),
    // `isPending` stays true while disabled (no countryId), so gate on countryId
    // to match the old behavior where "no country" meant "not loading".
    loading: !!countryId && (isPending || isFetching),
    error: (error as Error) ?? null,
    refetch,
  }
}
